import React from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from 'react-query';
import { MessageSquare, Mail, Smartphone, Facebook, Instagram, Inbox, Sparkles } from 'lucide-react';
import api from '../services/api';
import { formatDateTime } from '../utils/formatters';

// The lead's latest messages on every chat channel (SMS, email, Messenger,
// Instagram, WhatsApp), so whoever opens the lead sees the last exchange
// without leaving the page. Laid out like the Inbox thread: the customer on
// the left, the team on the right (AI messages tinted), oldest at the top.
// The Notes timeline below stays an internal notebook; full threads and
// replying live in the Inbox. Renders nothing for a lead with no messages.

const CHANNELS = {
  sms: { label: 'SMS', Icon: Smartphone },
  email: { label: 'Email', Icon: Mail },
  messenger: { label: 'Messenger', Icon: Facebook },
  instagram: { label: 'Instagram', Icon: Instagram },
  whatsapp: { label: 'WhatsApp', Icon: MessageSquare },
};

const senderOf = (m, customerName) => {
  if (m.direction === 'inbound') return customerName || 'Customer';
  if (m.sentByName) return m.sentByName;
  return m.ai ? 'AI' : 'Your team';
};

const bubbleClass = (m) => {
  if (m.direction === 'inbound') return 'bg-white text-gray-800 border border-gray-200 rounded-bl-sm';
  if (m.ai && !m.sentByName) return 'bg-violet-50 text-violet-950 border border-violet-200 rounded-br-sm';
  return 'bg-primary-600 text-white rounded-br-sm';
};

const LeadRecentMessages = ({ leadId, customerName, limit = 5 }) => {
  const { data } = useQuery(
    ['lead-recent-messages', leadId],
    () => api.get(`/conversations/lead/${leadId}/recent`, { params: { limit } }).then((r) => r.data),
    { enabled: !!leadId, staleTime: 30 * 1000 }
  );

  const messages = data?.messages || [];
  if (!messages.length) return null;
  const inboxUrl = `/inbox?leadId=${leadId}`;
  // The API returns newest first; a thread reads top to bottom.
  const thread = [...messages].reverse();

  return (
    <div className="card">
      <div className="card-header flex flex-wrap items-center justify-between gap-2">
        <h3 className="text-lg font-medium text-gray-900 flex items-center">
          <MessageSquare size={20} className="mr-2" />
          Recent messages
        </h3>
        <Link
          to={inboxUrl}
          className="inline-flex items-center gap-1 px-3 py-1.5 rounded-md border border-gray-300 bg-white text-xs font-medium text-gray-700 hover:bg-gray-50"
        >
          <Inbox size={14} /> Reply in Inbox
        </Link>
      </div>
      <div className="bg-gray-50 px-4 py-4 sm:px-6 space-y-3">
        {data.total > messages.length && (
          <div className="text-center">
            <Link to={inboxUrl} className="text-xs font-medium text-primary-600 hover:text-primary-700">
              View all {data.total} messages in the Inbox
            </Link>
          </div>
        )}
        {thread.map((m) => {
          const ch = CHANNELS[m.channel] || { label: m.channel, Icon: MessageSquare };
          const inbound = m.direction === 'inbound';
          const aiOnly = !inbound && m.ai && !m.sentByName;
          return (
            <div key={m._id} className={`flex ${inbound ? 'justify-start' : 'justify-end'}`}>
              <div className={`max-w-[85%] sm:max-w-[75%] flex flex-col ${inbound ? 'items-start' : 'items-end'}`}>
                <div className={`rounded-2xl px-3.5 py-2 text-sm leading-relaxed shadow-sm ${bubbleClass(m)}`}>
                  {m.channel === 'email' && m.subject && (
                    <div className={`text-xs font-semibold mb-0.5 truncate ${inbound || aiOnly ? 'text-gray-600' : 'text-primary-100'}`}>{m.subject}</div>
                  )}
                  <p className="whitespace-pre-wrap break-words line-clamp-4">{m.preview || '(no text)'}</p>
                </div>
                <div className={`mt-1 flex flex-wrap items-center gap-x-1.5 text-[11px] text-gray-500 ${inbound ? '' : 'justify-end'}`}>
                  {aiOnly && <Sparkles size={11} className="text-violet-500" />}
                  <span className={`font-medium ${inbound ? 'text-gray-700' : aiOnly ? 'text-violet-700' : 'text-gray-700'}`}>{senderOf(m, customerName)}</span>
                  <span aria-hidden="true">·</span>
                  <span className="inline-flex items-center gap-0.5"><ch.Icon size={11} /> {ch.label}</span>
                  <span aria-hidden="true">·</span>
                  <span>{formatDateTime(m.sentAt)}</span>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default LeadRecentMessages;
